<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, onMounted, ref, watch, type ComputedRef, type Ref } from "vue";
import {
  useRoute,
  useRouter,
  type RouteLocationNormalizedLoaded,
  type Router,
} from "vue-router";
import {
  loadEmailTemplates,
  loadNotificationEventDefaults,
  previewEmailTemplate,
  saveEmailTemplate,
  saveNotificationEventDefault,
  type EmailTemplate,
  type EmailTemplateKey,
  type EmailTemplatePreview,
  type NotificationEventDefault,
  type NotificationEventFamily,
  type NotificationSystemTemplateKey,
} from "../api/system-settings.js";
import { ApiRequestError } from "../api/http.js";
import AppShell from "../components/AppShell.vue";

type AdminNotificationTab = "defaults" | "templates";

interface DefaultForm {
  readonly eventFamily: NotificationEventFamily;
  thresholdMinutes: number | string;
  sendOpened: boolean;
  sendRecovery: boolean;
  openedTemplateKey: NotificationSystemTemplateKey;
  resolvedTemplateKey: NotificationSystemTemplateKey;
  updatedAt: string | null;
}

const notificationTemplateKeys: readonly EmailTemplateKey[] = [
  "internet_interruption_opened",
  "internet_interruption_resolved",
  "probe_unavailable_opened",
  "probe_unavailable_resolved",
  "notification_contact_opt_in",
];

const route: RouteLocationNormalizedLoaded = useRoute();
const router: Router = useRouter();
const activeTab: Ref<AdminNotificationTab> = ref("defaults");
const loading: Ref<boolean> = ref(false);
const saving: Ref<boolean> = ref(false);
const message: Ref<string | null> = ref(null);
const errorMessage: Ref<string | null> = ref(null);
const templates: Ref<EmailTemplate[]> = ref([]);
const selectedTemplateKey: Ref<EmailTemplateKey | null> = ref(null);
const templateSubject: Ref<string> = ref("");
const templateText: Ref<string> = ref("");
const templateHtml: Ref<string> = ref("");
const templatePreview: Ref<EmailTemplatePreview | null> = ref(null);
const previewing: Ref<boolean> = ref(false);
const forms: Ref<DefaultForm[]> = ref([
  {
    eventFamily: "internet_interruption",
    thresholdMinutes: 5,
    sendOpened: true,
    sendRecovery: true,
    openedTemplateKey: "internet_interruption_opened",
    resolvedTemplateKey: "internet_interruption_resolved",
    updatedAt: null,
  },
  {
    eventFamily: "probe_unavailable",
    thresholdMinutes: 5,
    sendOpened: true,
    sendRecovery: true,
    openedTemplateKey: "probe_unavailable_opened",
    resolvedTemplateKey: "probe_unavailable_resolved",
    updatedAt: null,
  },
]);

const tabs: readonly { readonly value: AdminNotificationTab; readonly label: string }[] = [
  { value: "defaults", label: "Defaults" },
  { value: "templates", label: "Templates" },
];
const sortedForms: ComputedRef<readonly DefaultForm[]> = computed(
  (): readonly DefaultForm[] =>
    [...forms.value].sort((left: DefaultForm, right: DefaultForm): number =>
      left.eventFamily.localeCompare(right.eventFamily),
    ),
);
const selectedTemplate: ComputedRef<EmailTemplate | null> = computed(
  (): EmailTemplate | null =>
    templates.value.find(
      (template: EmailTemplate): boolean => template.key === selectedTemplateKey.value,
    ) ?? null,
);

function errorText(error: unknown): string {
  return error instanceof ApiRequestError ? error.message : "Notification settings request failed.";
}

function familyLabel(eventFamily: NotificationEventFamily): string {
  return eventFamily === "internet_interruption" ? "Internet interruption" : "Probe unavailable";
}

function templateLabel(templateKey: string): string {
  return templateKey.replaceAll("_", " ");
}

function tabFromQuery(value: unknown): AdminNotificationTab {
  if (value === "defaults" || value === "templates") {
    return value;
  }
  return "defaults";
}

async function selectTab(tab: AdminNotificationTab): Promise<void> {
  activeTab.value = tab;
  await router.replace({
    name: "system-notifications",
    query: { ...route.query, tab },
  });
}

function openedTemplateFor(eventFamily: NotificationEventFamily): NotificationSystemTemplateKey {
  return eventFamily === "internet_interruption"
    ? "internet_interruption_opened"
    : "probe_unavailable_opened";
}

function resolvedTemplateFor(eventFamily: NotificationEventFamily): NotificationSystemTemplateKey {
  return eventFamily === "internet_interruption"
    ? "internet_interruption_resolved"
    : "probe_unavailable_resolved";
}

function placeholderToken(key: string): string {
  return `{{${key}}}`;
}

function applyDefaults(defaults: readonly NotificationEventDefault[]): void {
  forms.value = forms.value.map((form: DefaultForm): DefaultForm => {
    const item: NotificationEventDefault | undefined = defaults.find(
      (entry: NotificationEventDefault): boolean => entry.eventFamily === form.eventFamily,
    );
    if (item === undefined) return form;
    return {
      eventFamily: item.eventFamily,
      thresholdMinutes: Math.round(item.thresholdMs / 60000),
      sendOpened: item.sendOpened,
      sendRecovery: item.sendRecovery,
      openedTemplateKey: item.openedTemplateKey,
      resolvedTemplateKey: item.resolvedTemplateKey,
      updatedAt: item.updatedAt,
    };
  });
}

function applyTemplate(template: EmailTemplate): void {
  selectedTemplateKey.value = template.key;
  templateSubject.value = template.subjectTemplate;
  templateText.value = template.textBodyTemplate;
  templateHtml.value = template.htmlBodyTemplate ?? "";
  templatePreview.value = null;
}

async function loadNotifications(): Promise<void> {
  loading.value = true;
  errorMessage.value = null;
  message.value = null;
  try {
    const result: [readonly NotificationEventDefault[], readonly EmailTemplate[]] =
      await Promise.all([
        loadNotificationEventDefaults().then(
          (defaults): readonly NotificationEventDefault[] => defaults.defaults,
        ),
        loadEmailTemplates().then(
          (loadedTemplates): readonly EmailTemplate[] =>
            loadedTemplates.templates.filter(
              (template: EmailTemplate): boolean => notificationTemplateKeys.includes(template.key),
            ),
        ),
      ]);
    applyDefaults(result[0]);
    templates.value = [...result[1]];
    const selected: EmailTemplate | undefined =
      templates.value.find(
        (template: EmailTemplate): boolean => template.key === selectedTemplateKey.value,
      ) ?? templates.value[0];
    if (selected !== undefined) {
      applyTemplate(selected);
    }
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    loading.value = false;
  }
}

async function saveDefault(form: DefaultForm): Promise<void> {
  const minutes: number = Number(form.thresholdMinutes);
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1440) {
    errorMessage.value = "Threshold must be between 0 and 1440 minutes.";
    return;
  }
  saving.value = true;
  errorMessage.value = null;
  message.value = null;
  try {
    const saved: NotificationEventDefault = await saveNotificationEventDefault(form.eventFamily, {
      thresholdMs: minutes * 60000,
      sendOpened: form.sendOpened,
      sendRecovery: form.sendRecovery,
      openedTemplateKey: form.openedTemplateKey,
      resolvedTemplateKey: form.resolvedTemplateKey,
    });
    applyDefaults([saved]);
    message.value = `${familyLabel(form.eventFamily)} defaults saved.`;
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    saving.value = false;
  }
}

async function saveTemplate(): Promise<void> {
  const key: EmailTemplateKey | null = selectedTemplateKey.value;
  if (key === null) return;
  saving.value = true;
  errorMessage.value = null;
  message.value = null;
  try {
    const saved: EmailTemplate = await saveEmailTemplate(key, {
      enabled: true,
      subjectTemplate: templateSubject.value,
      textBodyTemplate: templateText.value,
      htmlBodyTemplate: templateHtml.value.trim() === "" ? null : templateHtml.value,
    });
    templates.value = templates.value.map(
      (template: EmailTemplate): EmailTemplate => template.key === saved.key ? saved : template,
    );
    applyTemplate(saved);
    message.value = `${saved.displayName} saved as revision ${saved.revision}.`;
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    saving.value = false;
  }
}

async function renderPreview(): Promise<void> {
  const key: EmailTemplateKey | null = selectedTemplateKey.value;
  if (key === null) return;
  previewing.value = true;
  errorMessage.value = null;
  message.value = null;
  try {
    templatePreview.value = await previewEmailTemplate(key, {
      enabled: true,
      subjectTemplate: templateSubject.value,
      textBodyTemplate: templateText.value,
      htmlBodyTemplate: templateHtml.value.trim() === "" ? null : templateHtml.value,
    });
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    previewing.value = false;
  }
}

onMounted(loadNotifications);
watch(
  (): unknown => route.query.tab,
  async (value: unknown): Promise<void> => {
    const normalizedTab: AdminNotificationTab = tabFromQuery(
      Array.isArray(value) ? value[0] : value,
    );
    activeTab.value = normalizedTab;
    if (value !== normalizedTab) {
      await router.replace({
        name: "system-notifications",
        query: { ...route.query, tab: normalizedTab },
      });
    }
  },
  { immediate: true },
);
</script>

<template>
  <AppShell>
    <header class="page-heading">
      <div>
        <p class="eyebrow">System</p>
        <h1>Notifications</h1>
        <p>Global notification defaults and fallback message templates.</p>
      </div>
    </header>

    <div class="page-tabs notification-tabs" role="tablist" aria-label="Notification administration">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        class="page-tab"
        :class="{ 'router-link-active': activeTab === tab.value }"
        @click="selectTab(tab.value)"
      >
        {{ tab.label }}
      </button>
    </div>

    <p v-if="errorMessage" class="form-error settings-load-error" role="alert">{{ errorMessage }}</p>
    <p v-if="message" class="settings-message" role="status">{{ message }}</p>
    <p v-if="loading" class="empty-state">Loading notification settings…</p>

    <section v-if="activeTab === 'defaults'" class="notification-policy-list">
      <form
        v-for="form in sortedForms"
        :key="form.eventFamily"
        class="settings-form notification-policy-card"
        @submit.prevent="saveDefault(form)"
      >
        <div class="settings-card__heading">
          <div>
            <p class="eyebrow">Default event behavior</p>
            <h2>{{ familyLabel(form.eventFamily) }}</h2>
          </div>
        </div>
        <label>
          <span>Threshold minutes</span>
          <input v-model="form.thresholdMinutes" type="number" min="0" max="1440" inputmode="numeric" required />
        </label>
        <label
          class="notification-template-field"
          :class="{ 'notification-template-field--disabled': !form.sendOpened }"
        >
          <span class="notification-template-toggle">
            <input v-model="form.sendOpened" type="checkbox" />
            <span>Send opened event</span>
          </span>
          <select v-model="form.openedTemplateKey" :disabled="!form.sendOpened">
            <option :value="openedTemplateFor(form.eventFamily)">
              {{ templateLabel(openedTemplateFor(form.eventFamily)) }}
            </option>
          </select>
        </label>
        <label
          class="notification-template-field"
          :class="{ 'notification-template-field--disabled': !form.sendRecovery }"
        >
          <span class="notification-template-toggle">
            <input v-model="form.sendRecovery" type="checkbox" />
            <span>Send resolved event</span>
          </span>
          <select v-model="form.resolvedTemplateKey" :disabled="!form.sendRecovery">
            <option :value="resolvedTemplateFor(form.eventFamily)">
              {{ templateLabel(resolvedTemplateFor(form.eventFamily)) }}
            </option>
          </select>
        </label>
        <div class="settings-actions settings-field--wide">
          <span v-if="form.updatedAt !== null" class="form-note">Updated {{ form.updatedAt }}</span>
          <button class="button button--primary" type="submit" :disabled="saving">
            {{ saving ? "Saving…" : "Save defaults" }}
          </button>
        </div>
      </form>
    </section>

    <section v-if="activeTab === 'templates'" class="content-panel settings-card email-template-card">
      <div class="settings-card__heading">
        <div>
          <p class="eyebrow">Fallback message content</p>
          <h2>Notification templates</h2>
        </div>
      </div>

      <div class="email-template-layout">
        <nav class="smtp-profile-list email-template-list" aria-label="Notification templates">
          <button
            v-for="template in templates"
            :key="template.key"
            type="button"
            class="smtp-profile-list__item"
            :class="{ 'smtp-profile-list__item--selected': selectedTemplateKey === template.key }"
            @click="applyTemplate(template)"
          >
            <span>
              <strong>{{ template.displayName }}</strong>
              <small>{{ template.category.replace("_", " ") }} · revision {{ template.revision }}</small>
            </span>
            <span class="status-dot status-dot--active"></span>
          </button>
        </nav>

        <form v-if="selectedTemplate" class="settings-form email-template-form" @submit.prevent="saveTemplate">
          <div class="email-template-summary">
            <div>
              <strong>{{ selectedTemplate.displayName }}</strong>
              <p>{{ selectedTemplate.description }}</p>
            </div>
          </div>

          <label><span>Subject</span><input v-model="templateSubject" maxlength="320" required /></label>
          <label><span>Plain-text body</span><textarea v-model="templateText" maxlength="100000" rows="11" required></textarea></label>
          <label><span>HTML body · optional</span><textarea v-model="templateHtml" maxlength="200000" rows="14" spellcheck="false"></textarea></label>

          <div class="template-placeholders">
            <strong>Available placeholders</strong>
            <p>Required placeholders must appear in at least one field. Values are HTML-escaped in the HTML body.</p>
            <div>
              <code v-for="placeholder in selectedTemplate.placeholders" :key="placeholder.key" :title="`${placeholder.description} · Example: ${placeholder.example}`">
                {{ placeholderToken(placeholder.key) }}
              </code>
            </div>
          </div>

          <div class="settings-actions">
            <small>Template key {{ selectedTemplate.key }}</small>
            <div>
              <button class="button button--quiet" type="button" :disabled="previewing || saving" @click="renderPreview">
                {{ previewing ? "Rendering…" : "Preview draft" }}
              </button>
              <button class="button button--primary" type="submit" :disabled="saving || previewing">
                {{ saving ? "Saving…" : "Save template" }}
              </button>
            </div>
          </div>

          <section v-if="templatePreview" class="email-template-preview" aria-live="polite">
            <div><span>Rendered subject</span><strong>{{ templatePreview.subject }}</strong></div>
            <div><span>Plain text</span><pre>{{ templatePreview.textBody }}</pre></div>
            <div v-if="templatePreview.htmlBody !== null"><span>HTML preview</span><iframe title="Rendered email HTML preview" sandbox="" :srcdoc="templatePreview.htmlBody"></iframe></div>
          </section>
        </form>
      </div>
    </section>
  </AppShell>
</template>
